import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TaskEntry } from '../TaskEntry'
import { taskToEntryData } from '../../utils/taskToEntryData'
import type { TaskStackViewProps } from './TaskStackView.types'
import {
  CARD_PITCH,
  computeDeckSlot,
} from './deckMath'
import './TaskStackView.scss'

// ──────────────────────────────────────────────────────────────────────────────
// Scroll engine constants — identical to the Clarify stack (ClarifyEntryList):
// wheel = Lenis-style exponential lerp, snap = time-based cubic ease-out.
// ──────────────────────────────────────────────────────────────────────────────
const SCROLL_LERP = 0.15
const SNAP_DURATION_MS = 280
const WHEEL_TAIL_MIN_STEP = 0.5

// Completion choreography: how long the checked focus card lingers before the
// deck advances, and how long after the advance snap it is removed.
const COMPLETE_LINGER_MS = 650
const COMPLETE_REMOVE_AFTER_MS = SNAP_DURATION_MS + 70

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

/**
 * Stack-mode deck ("Stapel") — one focused task card, the next tasks peeking
 * behind/above it as blank edge strips (max 3). Scrolling deals through the
 * queue: the focus card fades out downwards, the deck moves one forward.
 * Mechanism ported from the Clarify stack (single absolutely-positioned layer,
 * all motion derived from scrollTop — no CSS transitions), semantics inverted
 * to a card deck (see deckMath.ts).
 */
export function TaskStackView({
  tasks,
  isActive,
  initialActiveId,
  onActiveTaskChange,
  onToggleComplete,
  onDelete,
  onDuplicate,
  onContextMenuBadgeUpdate,
  getContextMenuMarks,
  menuShortcut, onMenuShortcutClose, onPlay,
  onSubmitEdit,
}: TaskStackViewProps) {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const isAutoScrollingRef = useRef(false)
  const smoothScrollToRef = useRef<((target: number) => void) | null>(null)
  // Instant scroll re-anchor (no animation) for queue-composition changes.
  const rebaseScrollToRef = useRef<((target: number) => void) | null>(null)
  const skipNextAutoScrollRef = useRef(false)
  const maxScrollTopRef = useRef(0)

  const [clientHeight, setClientHeight] = useState(0)

  // ── Completion linger ──
  // A just-checked focus card stays in the display queue (showing its checked
  // state) until the advance choreography ran; `fadeOutIds` marks cards that
  // leave without a next card to advance to (plain in-place fade via CSS).
  const [completingIds, setCompletingIds] = useState<ReadonlySet<string>>(new Set())
  const [fadeOutIds, setFadeOutIds] = useState<ReadonlySet<string>>(new Set())
  const completeTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  useEffect(() => () => {
    completeTimersRef.current.forEach(clearTimeout)
    completeTimersRef.current.clear()
  }, [])

  // ── Display queue: open tasks + lingering just-completed ones ──
  const queue = useMemo(
    () => tasks.filter(task =>
      task.pausedAt == null && (task.status !== 'completed' || completingIds.has(task.id))
    ),
    [tasks, completingIds],
  )
  const queueRef = useRef(queue)
  queueRef.current = queue

  // ── Entry card ──
  // The list hands its selection over via `initialActiveId` (see
  // useStackModeSelection). Resolved once on mount; an id that isn't in the
  // queue (completed / paused / another project) falls back to card 1.
  const [initialIdx] = useState(() => {
    const idx = initialActiveId ? queue.findIndex(task => task.id === initialActiveId) : -1
    return idx >= 0 ? idx : 0
  })

  const [activeId, setActiveId] = useState<string | null>(() => queue[initialIdx]?.id ?? null)
  const activeIdRef = useRef(activeId)
  activeIdRef.current = activeId

  // Card positions derive from this state (not from the DOM), so seeding it
  // with the entry card's offset paints the deck in place on frame 1 — no
  // visible snap from card 1 to the handed-over card.
  const [scrollTop, setScrollTop] = useState(() => initialIdx * CARD_PITCH)

  // Report the focus card upwards (mount + every change) so switching back to
  // the list can restore it as the selection.
  useEffect(() => {
    onActiveTaskChange?.(activeId)
  }, [activeId, onActiveTaskChange])

  const activeIdx = useMemo(() => {
    if (!activeId) return 0
    const idx = queue.findIndex(task => task.id === activeId)
    return idx >= 0 ? idx : 0
  }, [activeId, queue])
  const activeIdxRef = useRef(activeIdx)
  activeIdxRef.current = activeIdx

  const maxScrollTop = Math.max(0, queue.length - 1) * CARD_PITCH
  const contentHeight = maxScrollTop + Math.max(clientHeight, 1)
  maxScrollTopRef.current = maxScrollTop

  // ── Scroll listener: derive the active card from the scroll position ──
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const handler = () => {
      setScrollTop(prev => (prev === el.scrollTop ? prev : el.scrollTop))
      if (isAutoScrollingRef.current) return
      const nearestIdx = Math.round(el.scrollTop / CARD_PITCH)
      const clamped = Math.max(0, Math.min(queueRef.current.length - 1, nearestIdx))
      const nextId = queueRef.current[clamped]?.id
      if (nextId && nextId !== activeIdRef.current) {
        // User drove this via manual scroll — don't fight it with a snap.
        skipNextAutoScrollRef.current = true
        setActiveId(nextId)
      }
    }
    el.addEventListener('scroll', handler, { passive: true })
    return () => el.removeEventListener('scroll', handler)
  }, [])

  // Track the viewport height so the phantom scroll-content matches exactly.
  // (Inactive TabStack tabs measure 0 — the observer re-fires on activation.)
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const update = () => setClientHeight(el.clientHeight)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // ── rAF smooth-scroll engine (wheel lerp + programmatic snap + rebase) ──
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    let current = el.scrollTop
    let target = el.scrollTop
    let rafId: number | null = null
    let mode: 'idle' | 'wheel' | 'snap' = 'idle'

    let snapStart = 0
    let snapFrom = 0
    let snapTo = 0

    const clamp = (v: number): number =>
      Math.max(0, Math.min(maxScrollTopRef.current, v))

    const cancelRaf = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
        rafId = null
      }
    }

    const tickWheel = () => {
      if (mode !== 'wheel') {
        rafId = null
        return
      }
      const diff = target - current
      const rawStep = diff * SCROLL_LERP
      const step =
        Math.abs(rawStep) < WHEEL_TAIL_MIN_STEP
          ? Math.sign(diff) * WHEEL_TAIL_MIN_STEP
          : rawStep
      if (Math.abs(step) >= Math.abs(diff)) {
        current = target
        el.scrollTop = target
        isAutoScrollingRef.current = false
        mode = 'idle'
        rafId = null
        return
      }
      current += step
      el.scrollTop = current
      rafId = requestAnimationFrame(tickWheel)
    }

    const tickSnap = (now: number) => {
      if (mode !== 'snap') {
        rafId = null
        return
      }
      const progress = Math.min(1, (now - snapStart) / SNAP_DURATION_MS)
      const eased = easeOutCubic(progress)
      current = snapFrom + (snapTo - snapFrom) * eased
      el.scrollTop = current
      if (progress >= 1) {
        current = snapTo
        target = snapTo
        el.scrollTop = snapTo
        isAutoScrollingRef.current = false
        mode = 'idle'
        rafId = null
        return
      }
      rafId = requestAnimationFrame(tickSnap)
    }

    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      isAutoScrollingRef.current = false
      current = el.scrollTop
      // Inverted on purpose: wheel/swipe UP deals the deck forward (the focus
      // card lifts away), DOWN brings previous cards back.
      target = clamp(target - e.deltaY)
      if (mode !== 'wheel') {
        cancelRaf()
        mode = 'wheel'
        rafId = requestAnimationFrame(tickWheel)
      } else if (rafId === null) {
        rafId = requestAnimationFrame(tickWheel)
      }
    }

    smoothScrollToRef.current = (to: number) => {
      const clamped = clamp(to)
      const from = el.scrollTop
      if (Math.abs(clamped - from) < 0.3) return
      snapFrom = from
      snapTo = clamped
      snapStart = performance.now()
      current = from
      target = clamped
      isAutoScrollingRef.current = true
      cancelRaf()
      mode = 'snap'
      rafId = requestAnimationFrame(tickSnap)
    }

    // Instant re-anchor after queue-composition changes: identical layout under
    // a new index base, so no animation must play.
    rebaseScrollToRef.current = (to: number) => {
      const clamped = clamp(to)
      cancelRaf()
      mode = 'idle'
      isAutoScrollingRef.current = false
      current = clamped
      target = clamped
      el.scrollTop = clamped
      setScrollTop(clamped)
    }

    el.addEventListener('wheel', onWheel, { passive: false })

    return () => {
      el.removeEventListener('wheel', onWheel)
      cancelRaf()
      smoothScrollToRef.current = null
      rebaseScrollToRef.current = null
    }
  }, [])

  // Anchor the DOM scroll position + the engine's internal cursor on the entry
  // card. Deferred until the viewport is measured: while `clientHeight` is 0
  // the phantom scroll-content is ~1px tall and the browser clamps `scrollTop`
  // back to 0. Goes through `rebase` (not `el.scrollTop = …`) so the engine's
  // `target` moves along — otherwise the next wheel event would jump back.
  const didAnchorRef = useRef(initialIdx === 0)
  useEffect(() => {
    if (didAnchorRef.current || clientHeight === 0) return
    didAnchorRef.current = true
    rebaseScrollToRef.current?.(initialIdx * CARD_PITCH)
  }, [clientHeight, initialIdx])

  // Snap to the active card whenever it changes programmatically (keyboard /
  // completion advance). Scroll-driven changes skip the snap — and so does the
  // mount, where the deck is already anchored on the entry card above.
  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      return
    }
    if (skipNextAutoScrollRef.current) {
      skipNextAutoScrollRef.current = false
      return
    }
    smoothScrollToRef.current?.(activeIdx * CARD_PITCH)
  }, [activeId, activeIdx])

  // ── Queue-composition changes (task completed/deleted/moved elsewhere) ──
  // Re-anchor the scroll instantly so the deck doesn't animate a correction:
  // removing a card before/at the focus shifts every index by -1 while the
  // visual layout stays identical.
  const prevQueueIdsRef = useRef<string[]>(queue.map(task => task.id))
  useLayoutEffect(() => {
    const prevIds = prevQueueIdsRef.current
    const ids = queue.map(task => task.id)
    const changed = ids.length !== prevIds.length || ids.some((id, i) => id !== prevIds[i])
    prevQueueIdsRef.current = ids
    if (!changed || ids.length === 0) {
      if (ids.length === 0 && activeIdRef.current !== null) setActiveId(null)
      return
    }
    const currentActive = activeIdRef.current
    let idx = currentActive ? ids.indexOf(currentActive) : -1
    if (idx < 0) {
      // Active card left the queue — focus whatever now occupies its index.
      idx = Math.max(0, Math.min(ids.length - 1, activeIdxRef.current))
      setActiveId(ids[idx])
    }
    skipNextAutoScrollRef.current = true
    rebaseScrollToRef.current?.(idx * CARD_PITCH)
  }, [queue])

  // ── Keyboard: ↑/↓ deal through the deck ──
  // Capture phase + stopPropagation so the full-mode task-navigation shortcuts
  // (also window-level, bubble phase) don't double-handle the arrows.
  useEffect(() => {
    if (!isActive) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
      const len = queueRef.current.length
      if (len === 0) return
      e.preventDefault()
      e.stopPropagation()
      const delta = e.key === 'ArrowDown' ? 1 : -1
      const next = Math.max(0, Math.min(len - 1, activeIdxRef.current + delta))
      const nextId = queueRef.current[next]?.id
      if (nextId && nextId !== activeIdRef.current) setActiveId(nextId)
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [isActive])

  // ── Completion choreography ──
  const clearCompleteTimer = useCallback((taskId: string) => {
    const timer = completeTimersRef.current.get(taskId)
    if (timer) {
      clearTimeout(timer)
      completeTimersRef.current.delete(taskId)
    }
  }, [])

  const handleToggleComplete = useCallback((taskId: string) => {
    const task = queueRef.current.find(item => item.id === taskId)
    const wasCompleted = task?.status === 'completed'
    if (wasCompleted) {
      // Un-check during the linger — cancel the pending advance.
      clearCompleteTimer(taskId)
      setCompletingIds(prev => {
        const next = new Set(prev)
        next.delete(taskId)
        return next
      })
      setFadeOutIds(prev => {
        if (!prev.has(taskId)) return prev
        const next = new Set(prev)
        next.delete(taskId)
        return next
      })
    } else {
      setCompletingIds(prev => new Set(prev).add(taskId))
      clearCompleteTimer(taskId)
      completeTimersRef.current.set(taskId, setTimeout(() => {
        completeTimersRef.current.delete(taskId)
        const ids = queueRef.current.map(item => item.id)
        const idx = ids.indexOf(taskId)
        const finish = () => {
          setCompletingIds(prev => {
            const next = new Set(prev)
            next.delete(taskId)
            return next
          })
          setFadeOutIds(prev => {
            if (!prev.has(taskId)) return prev
            const next = new Set(prev)
            next.delete(taskId)
            return next
          })
        }
        if (idx >= 0 && idx + 1 < ids.length && activeIdRef.current === taskId) {
          // Advance the deck — the completed card plays the outgoing motion.
          setActiveId(ids[idx + 1])
          completeTimersRef.current.set(taskId, setTimeout(() => {
            completeTimersRef.current.delete(taskId)
            finish()
          }, COMPLETE_REMOVE_AFTER_MS))
        } else {
          // No next card (or the user scrolled away) — fade out in place.
          setFadeOutIds(prev => new Set(prev).add(taskId))
          completeTimersRef.current.set(taskId, setTimeout(() => {
            completeTimersRef.current.delete(taskId)
            finish()
          }, 250))
        }
      }, COMPLETE_LINGER_MS))
    }
    onToggleComplete(new Set([taskId]))
  }, [clearCompleteTimer, onToggleComplete])

  const handleDelete = useCallback((taskId: string) => {
    onDelete(new Set([taskId]))
  }, [onDelete])

  const scrollProgress = scrollTop / CARD_PITCH
  const isEmpty = queue.length === 0

  // The scroll container always mounts (even while empty) so the one-time
  // engine/listener effects find their element — the queue may fill later.
  return (
    <div className={`task-stack-view${isEmpty ? ' task-stack-view--empty' : ''}`}>
      {isEmpty && (
        <p className="task-stack-view__empty">{t('project.stackMode.empty')}</p>
      )}
      <div className="task-stack-view__scroll" ref={scrollRef}>
        <div
          className="task-stack-view__scroll-content"
          style={{ height: contentHeight }}
        >
          {queue.map((task, idx) => {
            const slot = computeDeckSlot(idx, scrollProgress)
            const fadingOut = fadeOutIds.has(task.id)
            const slotStyle: React.CSSProperties = {
              left: `${slot.insetX}px`,
              right: `${slot.insetX}px`,
              opacity: fadingOut ? 0 : slot.opacity,
              zIndex: slot.zIndex,
              // translate3d pins a stable GPU compositor layer (see Clarify).
              transform: `translate3d(0, ${scrollTop + slot.y}px, 0)`,
              pointerEvents: slot.interactive && !fadingOut ? 'auto' : 'none',
            }
            if (slot.clipHeight !== null) slotStyle.maxHeight = `${slot.clipHeight}px`
            return (
              <div
                key={task.id}
                className={`task-stack-view__slot${slot.clipHeight !== null ? ' task-stack-view__slot--clipped' : ''}${fadingOut ? ' task-stack-view__slot--fade-out' : ''}`}
                style={slotStyle}
              >
                <TaskEntry
                  // The focus card is permanently open (Figma 2172-17470):
                  // expanded chrome (outer + inner border, bottom action bar),
                  // no auto-focus, check completes immediately. Edit commits
                  // stay native (Enter / click-away, isDirty-guarded).
                  mode={task.id === activeId ? 'expanded' : 'collapsed'}
                  task={taskToEntryData(task)}
                  inProjectContext
                  autoFocus={false}
                  initialExpanded
                  onToggleComplete={handleToggleComplete}
                  onDelete={handleDelete}
                  onDuplicate={onDuplicate}
                  onContextMenuBadgeUpdate={onContextMenuBadgeUpdate}
                  getContextMenuMarks={getContextMenuMarks}
                  menuShortcut={menuShortcut}
                  onPlay={onPlay}
                  onMenuShortcutClose={onMenuShortcutClose}
                  onSubmitEdit={onSubmitEdit}
                />
                <div
                  className="task-stack-view__cover"
                  aria-hidden="true"
                  style={{
                    opacity: slot.coverOpacity,
                    // Theme-aware stand-in for the Figma edge tints (4–18% of a
                    // dark ink over the canvas): mix the text colour into the
                    // canvas so it works in light AND dark mode.
                    background: `color-mix(in oklab, var(--foreground) ${Math.round(slot.coverTint * 100)}%, var(--background))`,
                  }}
                />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
